import * as THREE from 'three';
import { sceneUnitsFromMm } from '../../utils/scaler';
import { defaultMaterial } from '../../utils/defaultMaterial';
import { isMemoryConstrained } from '../../utils/memoryUtils';

const azurePath = "https://gb3dassets.z6.web.core.windows.net/assetbundles/Components/";

export default class FbxAsyncGeometry {
    private static readonly loadedFbxCache = new Map<string, Promise<THREE.Group>>();

    group: THREE.Group;
    //placeholderMesh: THREE.Mesh;
    private dimensionsSU: THREE.Vector3 | null = null;
    private scaleToFit: boolean;
    private isMirrored: boolean;
    private initialRotation?: THREE.Quaternion;

    constructor(
        fbxUrl: string,
        layer: number,
        position: THREE.Vector3,
        dimensionsSU: THREE.Vector3 | undefined,
        options?: { scaleToFit?: boolean; isMirrored?: boolean; rotation?: THREE.Quaternion }
    ) {
        this.group = new THREE.Group();
        this.group.position.copy(position);
        this.group.quaternion.copy(options?.rotation ?? new THREE.Quaternion());
        this.group.layers.set(layer);

        this.scaleToFit = options?.scaleToFit ?? true;
        this.isMirrored = options?.isMirrored ?? false;
        
        this.dimensionsSU = dimensionsSU ?? null;

        // Placeholder cube
        const w = this.dimensionsSU?.x ?? sceneUnitsFromMm(1000);
        const h = this.dimensionsSU?.y ?? sceneUnitsFromMm(1000);
        const d = this.dimensionsSU?.z ?? sceneUnitsFromMm(1000);

        /* const geometry = new THREE.BoxGeometry(w, h, d);
        this.placeholderMesh = new THREE.Mesh(geometry, defaultMaterial);
        this.placeholderMesh.position.set(w / 2, h / 2, d / 2);
        this.placeholderMesh.layers.set(layer);

        this.group.add(this.placeholderMesh); */

        this.loadFbx(fbxUrl, layer).catch(() => {
            // Keep placeholder on failure
        });
    }

    private async loadFbx(fbxUrl: string, layer: number) {
        
        // Memory check disabled for now, since this is not the right way to stop FBX loading
        // We should load svg representations instead if FBX is not used
        // Also, memory check should be done at start and result be stored to state
        if (false && isMemoryConstrained()) {
            console.info(`FbxAsyncGeometry: Skipping FBX load for "${fbxUrl}" — memory constrained.`);
            return;
        }

        const { FBXLoader } = await import('three/examples/jsm/loaders/FBXLoader.js');

        if (!FbxAsyncGeometry.loadedFbxCache.has(fbxUrl)) {
            const loader = new FBXLoader();
            FbxAsyncGeometry.loadedFbxCache.set(fbxUrl, loader.loadAsync(azurePath + fbxUrl) as Promise<THREE.Group>);
        }

        const loadedFbx = await FbxAsyncGeometry.loadedFbxCache.get(fbxUrl)!;
        const fbxRoot = loadedFbx.clone(); // Clone to avoid modifying original loaded object
        // Port of the useEffect logic from FbxAssetRepresentationMesh

        // Ensure all meshes render on the desired layer
        fbxRoot.traverse(child => {
            child.layers.set(layer);
        });

        // Build wrapper (equivalent to modelRef group) with the same rotation as in the component
        const asset = new THREE.Group();
        asset.layers.set(layer);
        asset.rotation.set(Math.PI / 2, Math.PI, 0);
        asset.add(fbxRoot);

        // Clear placeholder
        this.group.clear();
        
        // Add the asset wrapper to the outer group
        this.group.add(asset);

        // If no need to scale/fit, we're done
        if (!this.scaleToFit) {
            return;
        }
        
        // Bake world transform so computations happen as if at origin
        asset.updateMatrixWorld(true);
        
        const worldMatrix = asset.matrixWorld.clone();
        const originalLocalMatrix = asset.matrix.clone();
        
        const inverseWorld = new THREE.Matrix4().copy(worldMatrix).invert();
        const newLocalMatrix = new THREE.Matrix4().copy(originalLocalMatrix).multiply(inverseWorld);
        
        asset.matrix.copy(newLocalMatrix);
        asset.matrix.decompose(asset.position, asset.quaternion, asset.scale);

        // Decompose for compatibility (updates position/quaternion/scale)

        asset.updateMatrixWorld(true);

        /* if (fbxUrl.includes("Default_SlidingDoor_Cabinet_4D_288_WhiteMirror")) {
            const breakHere = true;
            return;
        } */ 

        // Compute bounding box at origin
        const bbAtOrigin = new THREE.Box3().setFromObject(asset);
        const sizeAtOrigin = bbAtOrigin.getSize(new THREE.Vector3());
        const centerAtOrigin = bbAtOrigin.getCenter(new THREE.Vector3());

        // Target dimensions (scene units). If not provided, fall back to current size.
        const dimensions = this.dimensionsSU ?? sizeAtOrigin.clone();

        // Axis mapping (Y/Z swap due to FBX up axis)
        const scale = new THREE.Vector3(
            dimensions.x / (sizeAtOrigin.x || 1),
            dimensions.z / (sizeAtOrigin.y || 1),
            dimensions.y / (sizeAtOrigin.z || 1)
        );

        // Translate so scaled center goes to origin
        const scaledCenter = centerAtOrigin.clone().multiply(scale);
        const translationMatrix = new THREE.Matrix4()
            // move by the negative scaled center to bring the object to the origin
            .makeTranslation(scaledCenter.x, scaledCenter.y, scaledCenter.z).invert();

        // Move to center of target box (axis mapping signs as per component)
        const dimensionsCenter = dimensions.clone().multiplyScalar(0.5);
        const toDimensionsCenterMatrix = new THREE.Matrix4()
                                            .makeTranslation(   dimensionsCenter.x, 
                                                                -dimensionsCenter.z, 
                                                                -dimensionsCenter.y)
                                            .invert();

        // Apply translations on top of the original local matrix
        originalLocalMatrix.multiply(translationMatrix.clone());
        originalLocalMatrix.multiply(toDimensionsCenterMatrix.clone());

        asset.matrix.copy(originalLocalMatrix);
        asset.matrix.decompose(asset.position, asset.quaternion, asset.scale);

        // Apply final scaling and mirroring
        asset.scale.set(this.isMirrored ? -scale.x : scale.x, scale.y, scale.z);

        asset.updateMatrixWorld(true);
        

        // Prevent the remainder of the original implementation from running
        return;
        
    }
}